/**
 * One ICS-202 box while PRATUS AI writes it: the report-section lock look, with
 * the box's own control withheld so nobody edits under the draft. After a run
 * that could not draft the box, says why and offers a retry.
 */
import type React from 'react';
import type { ReactNode } from 'react';
import { Lock, SparklesIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';

import { useIcs202Draft } from './Ics202DraftContext';
import { ics202FieldLabel } from './ics202DraftPrompt';

export const ICS202_LOCKED_MESSAGE = 'PRATUS AI is generating…';

export const Ics202LockedNotice: React.FC = () => (
  <div
    className="tw:flex tw:flex-col tw:gap-1 tw:rounded-md tw:border tw:border-violet-400/40 tw:bg-violet-500/5 tw:px-2 tw:py-1.5"
    data-testid="ics202-locked-box"
  >
    <p className="tw:m-0 tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:font-medium tw:text-violet-200">
      <Lock className="tw:size-3.5" aria-hidden />
      {ICS202_LOCKED_MESSAGE}
    </p>
    <ReportsGeneratingPlaceholder message="Drafting this box from your selected sources..." />
  </div>
);

/** Why the box wasn't generated, plus a sparkle to try the box again. */
export const Ics202FailedNotice: React.FC<{
  source: string;
  label?: string;
}> = ({ source, label }) => {
  const draft = useIcs202Draft();
  const reason = draft?.failedSources.get(source);
  if (!draft || !reason) return null;
  const boxLabel = label ?? ics202FieldLabel(source);
  return (
    <div
      className="tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-muted-foreground"
      data-testid="ics202-failed-box"
    >
      <span>{reason}</span>
      {!draft.disabled ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Retry ${boxLabel}`}
          onClick={() => draft.openDraft([source], boxLabel)}
        >
          <SparklesIcon />
        </Button>
      ) : null}
    </div>
  );
};

export const Ics202LockedBox: React.FC<{
  source: string;
  label?: string;
  /** Shown under the control when the box is not locked (e.g. citations). */
  footer?: ReactNode;
  children: ReactNode;
}> = ({ source, label, footer, children }) => {
  const draft = useIcs202Draft();
  if (draft?.generatingSources.has(source)) return <Ics202LockedNotice />;
  return (
    <>
      {children}
      {footer}
      <Ics202FailedNotice source={source} label={label} />
    </>
  );
};
